import { useEventCallback } from '@mui/material/utils';
import {
  ColumnFilter,
  ColumnFiltersState,
  ColumnOrderState,
  ColumnSizingState,
  ColumnSort,
  ExpandedState,
  GroupingState,
  OnChangeFn,
  RowSelectionState,
  SortingState,
  Updater,
  VisibilityState,
} from '@tanstack/react-table';
import produce from 'immer';
import { uniq } from 'lodash';
import { compressToEncodedURIComponent } from 'lz-string';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { FilterType } from '@work4all/components/lib/dataDisplay/basic-table';
import { SELECTION_COLUMN_ID } from '@work4all/components/lib/dataDisplay/basic-table/utils/makeRowsSelectable';
import {
  EventType,
  sendAmplitudeData,
} from '@work4all/components/lib/utils/amplitude/amplitude';

import { useNavigate } from '@work4all/data/lib/hooks';

import { Work4AllEntity } from '@work4all/models/lib/additionalEnums/Work4AllEntity.entity';
import { SortDirection, SortOption } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { SavedTableConfig, settings, useSetting } from '../../../../settings';
import { deserializeFilter } from '../../../../utils/deserializeFilter';
import { serializeFilter, tryParseSettings } from '../../../list-page/ListPage';
import { useListMatch } from '../../../list-page/use-list-match';
import { OBJECT_LOCK_COLUMN_ID } from '../../generic-column/GenericColumns';
import { IUseTableConfigOptions } from '../../use-table-config';
import { DataTableColumnConfig } from '../DataTableColumnConfig';

export function useDataTableState(options: {
  enableURLState: boolean;
  entityType: Entities;
  layout: IUseTableConfigOptions<any>['layout'];
  columnConfigs: DataTableColumnConfig<Work4AllEntity>[];
  defaultSort?: SortOption[];
  defaultColumnFilters?: ColumnFilter[];
  defaultGrouping?: GroupingState;
  autoColumns: string[];
  isResizing: () => boolean;
}) {
  const {
    enableURLState,
    entityType,
    layout,
    columnConfigs,
    defaultSort,
    defaultColumnFilters: defaultColumnFiltersProp,
    defaultGrouping = [],
    autoColumns,
    isResizing,
  } = options;

  const [columnFiltersState, setColumnFiltersState] =
    useState<ColumnFiltersState>([]);
  const [sortingState, setSortingState] = useState<SortingState>(() =>
    parseSortingState(defaultSort)
  );
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [grouping, setGrouping] = useState<GroupingState>(defaultGrouping);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [globalFilter, setGlobalFilter] = useState<string>('');

  const [isUnderPressSelect, setUnderPressSelect] = useState(false);

  const selectedRowsCount = Object.entries(rowSelection).reduce(
    (acc, [, value]) => (value ? acc + 1 : acc),
    0
  );

  useEffect(() => {
    if (selectedRowsCount === 0) {
      setUnderPressSelect(false);
    }
  }, [selectedRowsCount]);

  const savedTableConfig = useSetting(
    settings.tableConfig({ entityType, layout })
  );

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const settingsParam = searchParams.get(SETTINGS_PARAM_NAME);

  const urlState = useMemo(() => {
    return tryParseSettings(settingsParam);
  }, [settingsParam]);

  const columnFilters = useMemo<ColumnFilter[]>(() => {
    if (!urlState?.filter) return [];

    return urlState.filter
      .map((f) => {
        const column = columnConfigs.find((c) => c.id === f.id);
        if (!column) return null;

        const filterType = column.filterType;
        const deserialized = deserializeFilter(f, filterType);
        return { id: column.id, value: { ...deserialized, filterType } };
      })
      .filter(Boolean) as any[];
  }, [columnConfigs, urlState?.filter]);

  const sorting = useMemo(() => {
    return urlState?.sort ?? parseSortingState(defaultSort);
  }, [defaultSort, urlState?.sort]);

  const handleURLStateChange = (
    change: Partial<{
      filters: ColumnFiltersState;
      sorting: SortingState;
    }>
  ) => {
    const newValue = {
      filters: change.filters ?? columnFilters,
      sorting: change.sorting ?? sorting,
    };

    const serialized = serializeTableStateV8(newValue);

    const url = new URL(window.location.href);

    if (serialized == null) {
      url.searchParams.delete(SETTINGS_PARAM_NAME);
    } else {
      const stringified = compressToEncodedURIComponent(
        JSON.stringify(serialized)
      );
      url.searchParams.set(SETTINGS_PARAM_NAME, stringified);
    }

    if (url.href !== window.location.href) {
      navigate({ search: url.search }, { replace: true });
    }
  };

  const handleColumnFiltersChange = (updater: Updater<ColumnFiltersState>) => {
    const newColumnFilters =
      typeof updater === 'function' ? updater(columnFilters) : updater;
    handleURLStateChange({ filters: newColumnFilters });
  };

  const handleSortingChange = (updater: Updater<SortingState>) => {
    const newSorting = applyUpdater(updater, sorting);
    handleURLStateChange({ sorting: newSorting });
  };

  const savedURLStateRef = useRef({ columnFilters, sorting });

  const listMatch = useListMatch();
  const shouldUseCurrentURL = listMatch.isBaseListPageUrl;

  useEffect(() => {
    if (enableURLState && shouldUseCurrentURL) {
      savedURLStateRef.current = { columnFilters, sorting };
    }
  }, [columnFilters, enableURLState, shouldUseCurrentURL, sorting]);

  const [hasChangedFilters, setHasChangedFilters] = useState(false);

  // Default ClosedStatus filter
  const { value: hideClosedEntitiesSetting } = useSetting(
    settings.hideClosedEntities({ entityType })
  );

  const defaultColumnFilters = useMemo<ColumnFiltersState>(() => {
    if (!hideClosedEntitiesSetting) return [];

    const column = columnConfigs.find((column) => {
      return column.filterType === FilterType.ClosedStatus;
    });
    if (!column) return [];

    return [
      { id: column.id, value: { filterType: column.filterType, value: true } },
    ];
  }, [columnConfigs, hideClosedEntitiesSetting]);

  const urlOrStateColumnFilters = enableURLState
    ? shouldUseCurrentURL
      ? columnFilters
      : savedURLStateRef.current.columnFilters
    : columnFiltersState;
  const resolvedColumnFilters =
    urlOrStateColumnFilters.length === 0 && !hasChangedFilters
      ? defaultColumnFiltersProp ?? defaultColumnFilters
      : urlOrStateColumnFilters;
  const resolvedSorting = enableURLState
    ? shouldUseCurrentURL
      ? sorting
      : savedURLStateRef.current.sorting
    : sortingState;

  const defaultColumnVisibility = useMemo(() => {
    const columnVisibility: VisibilityState = {};
    for (const column of columnConfigs) {
      if (column.defaultHidden) {
        columnVisibility[column.id] = false;
      }
    }
    return columnVisibility;
  }, [columnConfigs]);

  const userColumnVisibility = useMemo(() => {
    const columnVisibility: VisibilityState = {};
    if (savedTableConfig.value?.columns) {
      for (const [column, config] of Object.entries(
        savedTableConfig.value?.columns
      )) {
        if (config.hidden !== undefined) {
          columnVisibility[column] = !config.hidden;
        }
      }
    }

    for (const column of autoColumns) {
      delete columnVisibility[column];
    }

    return columnVisibility;
  }, [savedTableConfig.value, autoColumns]);

  const columnVisibility = useMemo<VisibilityState>(() => {
    return { ...defaultColumnVisibility, ...userColumnVisibility };
  }, [defaultColumnVisibility, userColumnVisibility]);

  const defaultColumnOrder = useMemo<ColumnOrderState>(() => {
    return [];
  }, []);

  const userColumnOrder = useMemo<ColumnOrderState>(() => {
    return savedTableConfig.value?.order ?? null;
  }, [savedTableConfig.value]);

  const userColumnSizing = useMemo<ColumnSizingState>(() => {
    if (!savedTableConfig.value?.columns) return {};

    const columnSizing: ColumnSizingState = {};

    for (const [column, config] of Object.entries(
      savedTableConfig.value?.columns
    )) {
      if (config.width !== undefined) {
        columnSizing[column] = config.width;
      }
    }

    return columnSizing;
  }, [savedTableConfig.value]);

  const columnOrder = useMemo<ColumnOrderState>(() => {
    if (userColumnOrder) {
      return userColumnOrder.filter((id) => !autoColumns.includes(id));
    }

    return defaultColumnOrder.filter((id) => !autoColumns.includes(id));
  }, [autoColumns, defaultColumnOrder, userColumnOrder]);

  const [columnSizing, setColumnSizing] = useState(userColumnSizing);

  const handleRowSelectionChange = useEventCallback<
    OnChangeFn<RowSelectionState>
  >((updater) => {
    const newValue =
      typeof updater === 'function' ? updater(rowSelection) : updater;
    setRowSelection(newValue);
  });

  const handleColumnFiltersChangeWrapper = (
    updater: Updater<ColumnFiltersState>
  ) => {
    const newColumnFilters = applyUpdater(updater, resolvedColumnFilters);

    // Report used filters
    for (const filter of newColumnFilters) {
      if (!resolvedColumnFilters.find((f) => f.id === filter.id)) {
        sendAmplitudeData(EventType.FilterList, {
          name: entityType,
          filterElement: filter.id,
        });
      }
    }

    const handler = enableURLState
      ? handleColumnFiltersChange
      : setColumnFiltersState;

    handler(newColumnFilters);
  };

  const columnPinning = useMemo(() => {
    const left: string[] = [];
    const right: string[] = [];

    for (const col of columnConfigs) {
      switch (col.sticky) {
        case 'left':
          left.push(col.id);
          break;
        case 'right':
          right.push(col.id);
          break;
      }
    }

    left.unshift(SELECTION_COLUMN_ID);
    right.push(OBJECT_LOCK_COLUMN_ID);

    return { left: uniq(left), right: uniq(right) };
  }, [columnConfigs]);

  const hasResizedRef = useRef(false);

  useEffect(() => {
    if (!isResizing()) {
      if (hasResizedRef.current) {
        // Update user settings
        hasResizedRef.current = false;

        const newSettingValue = produce(
          savedTableConfig.value ?? DEFAULT_SETTING,
          (draft) => {
            draft.columns ??= {};

            for (const [column, size] of Object.entries(columnSizing)) {
              draft.columns[column] ??= {};
              draft.columns[column].width = size;
            }
          }
        );

        savedTableConfig.set(newSettingValue);
      } else {
        setColumnSizing(userColumnSizing);
      }
    } else {
      hasResizedRef.current = true;
    }
  }, [isResizing, userColumnSizing, columnSizing, savedTableConfig]);

  return {
    state: {
      columnFilters: resolvedColumnFilters,
      sorting: resolvedSorting,
      rowSelection,
      grouping,
      expanded,
      columnPinning: columnPinning,
      globalFilter: globalFilter,
      columnVisibility,
      columnOrder,
      columnSizing: columnSizing,
    },

    onColumnFiltersChange: (updater) => {
      // Reset selection when filters change
      setHasChangedFilters(true);
      handleRowSelectionChange({});
      handleColumnFiltersChangeWrapper(updater);
    },

    onSortingChange: (updater) => {
      // Reset selection when filters change
      handleRowSelectionChange({});
      if (enableURLState) {
        handleSortingChange(updater);
      } else {
        setSortingState(updater);
      }
    },
    onRowSelectionChange: handleRowSelectionChange,
    onExpandedChange: setExpanded,
    onGroupingChange: (updater) => {
      // Reset selection when filters change
      handleRowSelectionChange({});
      setGrouping(updater);
    },
    onGlobalFilterChange: (updater) => {
      // Reset selection when filters change
      handleRowSelectionChange({});
      setGlobalFilter(updater);
    },
    onColumnVisibilityChange: (updater) => {
      const newColumnVisibility =
        typeof updater === 'function' ? updater(userColumnVisibility) : updater;

      const newSettingValue = produce(
        savedTableConfig.value ?? DEFAULT_SETTING,
        (draft) => {
          draft.columns ??= {};

          for (const [column, visible] of Object.entries(newColumnVisibility)) {
            draft.columns[column] ??= {};
            draft.columns[column].hidden = !visible;
          }
        }
      );

      savedTableConfig.set(newSettingValue);
    },
    onColumnOrderChange: (updater) => {
      const newColumnOrder =
        typeof updater === 'function' ? updater(columnOrder) : updater;

      const newSettingValue = produce(
        savedTableConfig.value ?? DEFAULT_SETTING,
        (draft) => {
          draft.order = newColumnOrder;
        }
      );

      savedTableConfig.set(newSettingValue);
    },
    onColumnSizingChange: setColumnSizing,

    isUnderPressSelect: isUnderPressSelect,
    setUnderPressSelect: setUnderPressSelect,
  };
}

const SETTINGS_PARAM_NAME = 'settings';
const DEFAULT_SETTING: SavedTableConfig = { columns: {} };

function parseSortingState(sort: SortOption[]): ColumnSort[] {
  if (sort == null) return [];
  return sort.map<ColumnSort>((s) => {
    return { id: s.field, desc: s.direction === SortDirection.DESCENDING };
  });
}

function serializeTableStateV8(tableState: {
  filters: ColumnFiltersState;
  sorting: SortingState;
}) {
  const settings = {
    filter: tableState.filters?.map((filter) => {
      const serialized = serializeFilter(filter);

      return serialized;
    }),
    sort: tableState.sorting,
  };

  if (settings.filter && settings.filter.length === 0) {
    delete settings.filter;
  }

  if (settings.sort && settings.sort.length === 0) {
    delete settings.sort;
  }

  if (Object.keys(settings).length === 0) {
    return null;
  }

  return settings;
}

function applyUpdater<T extends object | Array<unknown>>(
  updaterOrValue: Updater<T>,
  oldValue: T
): T {
  return typeof updaterOrValue === 'function'
    ? updaterOrValue(oldValue)
    : updaterOrValue;
}
